import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'

/** Must match TURNSTILE_ACTIONS on the API: the server rejects a token minted
 *  for a different action, so a short-form token can't be replayed on login. */
export type TurnstileAction = 'short-form' | 'driver-login'

/** Why `onToken` is being called with null. */
export type TurnstileTokenLossReason =
  | 'expired'
  | 'error'
  | 'timeout'
  | 'reset'
  /** The api.js script failed to load (ad blocker, offline, blocked CDN). */
  | 'script-blocked'

interface TurnstileRenderOptions {
  sitekey: string
  action?: string
  callback?: (token: string) => void
  'expired-callback'?: () => void
  'error-callback'?: () => void
  'timeout-callback'?: () => void
  theme?: 'light' | 'dark' | 'auto'
  size?: 'normal' | 'compact' | 'flexible'
  appearance?: 'always' | 'execute' | 'interaction-only'
}

interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: TurnstileRenderOptions
  ) => string | undefined
  reset: (widgetId?: string) => void
  remove: (widgetId?: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
    __dnTurnstileOnLoad?: () => void
  }
}

const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=__dnTurnstileOnLoad'

let scriptPromise: Promise<TurnstileApi> | null = null

/** Loads the Turnstile script once per page and resolves with its API. */
export const loadTurnstile = (): Promise<TurnstileApi> => {
  if (window.turnstile) return Promise.resolve(window.turnstile)
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise<TurnstileApi>((resolve, reject) => {
    window.__dnTurnstileOnLoad = () => {
      if (window.turnstile) resolve(window.turnstile)
      else reject(new Error('Turnstile loaded without an API'))
    }
    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onerror = () => {
      scriptPromise = null
      reject(new Error('Failed to load Turnstile'))
    }
    document.head.appendChild(script)
  })
  return scriptPromise
}

export const getTurnstileSiteKey = (): string | undefined =>
  process.env.TURNSTILE_SITE_KEY || undefined

export interface TurnstileWidgetProps {
  action: TurnstileAction
  /** Fires with a fresh token, or null (with a reason) when the token expired,
   *  errored, was reset, or the script never loaded. Tokens are single-use:
   *  call `reset()` after each submit. */
  onToken: (token: string | null, reason?: TurnstileTokenLossReason) => void
  /** Show the widget even when Cloudflare could verify silently. Used after a
   *  token wait expires so the driver can see and complete the check. Toggling
   *  it re-renders the widget. */
  visible?: boolean
  className?: string
}

export interface TurnstileWidgetHandle {
  reset: () => void
}

/**
 * Cloudflare Turnstile widget. Renders nothing when TURNSTILE_SITE_KEY is
 * unset (local dev without keys), so callers can mount it unconditionally.
 *
 * `appearance: 'interaction-only'` keeps it invisible for the vast majority of
 * drivers; only a browser Cloudflare can't verify silently sees a checkbox.
 * Pass `visible` to force it on screen (after a token wait expired).
 */
const TurnstileWidget = forwardRef<TurnstileWidgetHandle, TurnstileWidgetProps>(
  ({ action, onToken, visible = false, className }, ref) => {
    const siteKey = getTurnstileSiteKey()
    const containerRef = useRef<HTMLDivElement>(null)
    const widgetIdRef = useRef<string | undefined>(undefined)
    const apiRef = useRef<TurnstileApi | null>(null)
    // Keep the latest callback without re-rendering the widget on each render.
    const onTokenRef = useRef(onToken)
    onTokenRef.current = onToken

    useImperativeHandle(ref, () => ({
      reset: () => {
        if (apiRef.current && widgetIdRef.current !== undefined) {
          apiRef.current.reset(widgetIdRef.current)
          onTokenRef.current(null, 'reset')
        }
      },
    }))

    useEffect(() => {
      if (!siteKey || !containerRef.current) return
      let cancelled = false

      loadTurnstile()
        .then((api) => {
          if (cancelled || !containerRef.current) return
          apiRef.current = api
          widgetIdRef.current = api.render(containerRef.current, {
            sitekey: siteKey,
            action,
            theme: 'light',
            size: 'flexible',
            appearance: visible ? 'always' : 'interaction-only',
            callback: (token) => onTokenRef.current(token),
            'expired-callback': () => onTokenRef.current(null, 'expired'),
            'error-callback': () => onTokenRef.current(null, 'error'),
            'timeout-callback': () => onTokenRef.current(null, 'timeout'),
          })
        })
        .catch(() => {
          // Script blocked or offline: the API falls open while log-only and
          // returns a clear error once enforced. Nothing to render here.
          onTokenRef.current(null, 'script-blocked')
        })

      return () => {
        cancelled = true
        if (apiRef.current && widgetIdRef.current !== undefined) {
          try {
            apiRef.current.remove(widgetIdRef.current)
          } catch {
            // Already gone; ignore.
          }
        }
        widgetIdRef.current = undefined
      }
    }, [siteKey, action, visible])

    if (!siteKey) return null
    return (
      <div
        ref={containerRef}
        className={className}
        data-testid="turnstile-widget"
      />
    )
  }
)
TurnstileWidget.displayName = 'TurnstileWidget'

export default TurnstileWidget
